import {
  IReportHeartbeatTask,
  IReportHeartbeatTaskProps,
  OnErrorFn,
  generateUUID,
  ReportHearbeatResponse,
  ClientErrorCode,
  ISessionManager,
  WsError,
  WsErrorCodes,
  WsErrorTypes,
  IHeartBeat,
  ILogger,
  IpFallbackEndpoint,
} from '@amzn/work-spaces-web-client-core-lib';

interface ReportHeartbeatTaskDeps {
  logger: ILogger;
  heartbeat: IHeartBeat;
  sessionManager: ISessionManager;
}

const REPORT_HEARTBEAT_MAX_RETRY = 5;
const REPORT_HEARTBEAT_RETRY_DELAY = 1000; // 1 second
const TOKEN_VALID_TIME = 20 * 60 * 1000; // 20 minutes

export class ReportHeartbeatTask implements IReportHeartbeatTask {
  private readonly logger: ILogger;
  private readonly heartbeat: IHeartBeat;
  private readonly sessionManager: ISessionManager;

  private onError?: OnErrorFn;

  private taskId: string | null;
  private timerId: NodeJS.Timeout | null;

  constructor({ logger, heartbeat, sessionManager }: ReportHeartbeatTaskDeps) {
    this.logger = logger;
    this.heartbeat = heartbeat;
    this.sessionManager = sessionManager;

    this.taskId = null;
    this.timerId = null;
  }

  start({ onError }: IReportHeartbeatTaskProps) {
    // Clear any existing timer before starting a new task
    this.stop();

    this.taskId = generateUUID();
    this.onError = onError;
    this.reportHeartbeatTask(this.taskId);
  }

  stop() {
    this.taskId = null;
    this.onError = undefined;

    // Clear any pending timer
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  private async reportHeartbeatTask(taskId: string) {
    const sessionContext = this.sessionManager.get('sessionContext')!;

    try {
      const {
        sessionToken,
        heartBeatContext: { intervalInSeconds },
      } = await this.reportHeartbeatWithRetry(taskId);
      const newAuthToken = {
        Value: sessionToken,
        ExpirationTimestamp: Date.now() + TOKEN_VALID_TIME,
      };
      this.sessionManager.set({
        authToken: newAuthToken,
        sessionContext: {
          ...sessionContext,
          AuthToken: newAuthToken,
        },
      });
      if (this.shouldContinue(taskId)) {
        this.timerId = setTimeout(() => {
          this.reportHeartbeatTask(taskId);
        }, intervalInSeconds * 1000);
      }
    } catch (error) {
      this.onError?.(error);
      this.stop();
    }
  }

  private async reportHeartbeatWithRetry(taskId: string, retries: number = 0) {
    const heartBeatEndpoint = this.sessionManager.get('heartBeatEndpoint');

    if (!heartBeatEndpoint) {
      throw new WsError(
        WsErrorTypes.ERROR_TYPE_HEARTBEAT,
        WsErrorCodes.ERROR_MISSING_HEARTBEAT_ENDPOINT,
        ClientErrorCode.ReportHeartbeatMissingEndpoint
      );
    }

    // A reload restores the endpoint from JSON without its methods, so build it
    // from its URLs on every call.
    const { ipv4Endpoint, ipv6Endpoint } = heartBeatEndpoint;
    const endpoint = new IpFallbackEndpoint(ipv4Endpoint[0], ipv6Endpoint[0]);

    try {
      return await this.heartbeat.reportHeartBeat(endpoint);
    } catch (error: unknown) {
      if (
        error instanceof WsError &&
        error.clientErrorCode ===
          ClientErrorCode.ReportHeartbeatRetriableError &&
        this.shouldContinue(taskId) &&
        retries < REPORT_HEARTBEAT_MAX_RETRY
      ) {
        this.logger.info(
          `Retriable error happened for ReportHeartbeat. Retry count: ${retries}`
        );
        const retryResult = await new Promise((resolve, reject) => {
          setTimeout(() => {
            this.reportHeartbeatWithRetry(taskId, retries + 1)
              .then(resolve)
              .catch(reject);
          }, REPORT_HEARTBEAT_RETRY_DELAY * 2 ** retries);
        });
        return retryResult as ReportHearbeatResponse;
      }

      throw error;
    }
  }

  private shouldContinue(taskId: string) {
    return this.taskId === taskId;
  }
}
